(function (root) {
  'use strict';
  const PRICE = { single: 6990, volume: 5990, once: 7990, shipping: 400 };
  function configurePrice(cents) {
    if (!Number.isSafeInteger(cents) || cents <= 2000) return false;
    PRICE.once = cents; PRICE.single = cents - 1000; PRICE.volume = cents - 2000;
    return true;
  }
  const DISCOUNTS = Object.freeze({ basic: '20OFF', volume: '33OFF', retired: 'LAUNCHOFFER' });
  function subscriptionPrice(row, allocation) {
    if (!Number.isSafeInteger(row.boxes) || row.boxes < 1) throw new Error('Enter a whole number of subscription boxes.');
    const volume = row.boxes >= 3;
    const unit = allocation ? allocation.price : volume ? PRICE.volume : PRICE.single;
    return { unit, subtotal: unit * row.boxes, volume,
      amount: PRICE.once - unit, saving: Math.floor((1 - unit / PRICE.once) * 100) };  // round down: never overstate a saving
  }
  function subscriptionBoxes(cart) {
    return cart.items.filter(item => root.HouseholdPlanner.managed(item)).reduce((sum, item) => sum + item.quantity, 0);
  }
  function cartShipping(cart) {
    return !cart.items.length || cart.items.some(item => item.selling_plan_allocation) ? 0 : PRICE.shipping;
  }
  function cartDiscount(cart) {
    const boxes = subscriptionBoxes(cart);
    return boxes ? subscriptionPrice({boxes}) : null;
  }
  function discountCodes(cart) {
    const applications = [...(cart.cart_level_discount_applications || []), ...cart.items.flatMap(item =>
      (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))];
    return [...new Set([...(cart.discount_codes || []).map(discount => discount.code),
      ...applications.filter(application => application?.type === 'discount_code').map(application => application.title)]
      .filter(Boolean).map(code => code.toUpperCase()))];
  }
  function selectedCodes(cart) {
    const owned = Object.values(DISCOUNTS);
    const other = discountCodes(cart).filter(code => !owned.includes(code));
    return other;
  }
  // Both the compact panel data and native Liquid/Ajax allocation shape are supported.
  // Reject first-order-only or prepaid pricing: this UI promises one recurring total.
  function validAllocation(allocation, row, cart = false) {
    const id = Number(allocation?.id ?? allocation?.selling_plan?.id);
    if (!row || !root.HouseholdPlanner.planId(row) || id !== root.HouseholdPlanner.planId(row)) return false;
    const price = allocation?.price;
    if (!Number.isSafeInteger(price) || price <= 0 || price >= PRICE.once) return false;
    const recurring = allocation.recurring ?? allocation.selling_plan?.recurring_deliveries;
    if (recurring === false || (!cart && recurring !== true)) return false;
    const checkout = allocation.checkout ?? allocation.checkout_charge_amount;
    const delivery = allocation.delivery ?? allocation.per_delivery_price;
    if ((!cart || checkout !== undefined) && checkout !== price) return false;
    if ((!cart || delivery !== undefined) && delivery !== price) return false;
    const adjustments = allocation.adjustments ?? allocation.price_adjustments;
    return (!adjustments || Array.isArray(adjustments) && adjustments.every(p => p.price === price));
  }
  function validBase(item) {
    const allocation = item.selling_plan_allocation;
    const weeks = root.HouseholdPlanner.planWeeks(allocation?.selling_plan?.id);
    return root.HouseholdPlanner.managed(item) && Number.isSafeInteger(item.quantity) && item.quantity > 0 &&
      validAllocation(allocation, {boxes:item.quantity, weeks}, true);
  }
  function validDiscount(cart) {
    if (cart.currency !== 'SGD' || discountCodes(cart).some(code => Object.values(DISCOUNTS).includes(code))) return false;
    // Other items and their discounts remain untouched. A stacked discount on this
    // subscription needs explicit review rather than being silently removed.
    return cart.items.filter(item => root.HouseholdPlanner.managed(item)).every(item => validBase(item) &&
      item.final_line_price === item.quantity * item.selling_plan_allocation.price);
  }
  const DOSES = { small: 0.5, medium: 1, large: 2 };
  const WEEKS = [4, 8, 12, 24];
  const MAX_OPTIONS = 5;
  const MAX_DOGS = 3;
  const NORMAL_BOX_LIMIT = 12;
  function validate(counts) {
    if (!counts || Array.isArray(counts) || Object.keys(counts).some(k => !Object.hasOwn(DOSES,k))) throw new Error('Use small, medium and large dog counts.');
    for (const key of Object.keys(DOSES)) {
      if (!Number.isSafeInteger(counts[key]) || counts[key] < 0 || counts[key] > MAX_DOGS) throw new Error(`Enter a whole dog count from 0 to ${MAX_DOGS}.`);
    }
    if (counts.small + counts.medium + counts.large > MAX_DOGS) throw new Error(`You can add up to ${MAX_DOGS} dogs.`);
    return counts;
  }
  function daily(counts) { validate(counts); return counts.small / 2 + counts.medium + counts.large * 2; }
  function plan(weeks, dose) {
    if (!WEEKS.includes(weeks) || !Number.isFinite(dose) || dose <= 0 || !Number.isSafeInteger(dose * 2)) throw new Error('Choose a supported cycle and a positive half-sachet daily amount.');
    const days = weeks * 7;
    const boxes = days * dose / 28;
    if (!Number.isSafeInteger(boxes)) throw new Error('This supply does not fit into whole boxes.');
    const unit = boxes >= 3 ? PRICE.volume : PRICE.single;
    const shipping = 0;
    const surplus = boxes * 28 - days * dose;
    return { boxes, dose, weeks, days, unit, shipping, subtotal: boxes * unit, total: boxes * unit + shipping,
      sachets: boxes * 28, surplus, coverage: boxes * 28 / dose,
      surplusDays: surplus / dose, annualSurplus: surplus * 365 / days,
      exact: surplus === 0 };
  }
  function recommend(counts) {
    const dose = daily(counts);
    if (!dose) return null;
    const dogs = counts.small + counts.medium + counts.large;
    if (dogs === 1) {
      const size = Object.keys(DOSES).find(key => counts[key]);
      const cycles = {small:[24,8],medium:[12,4],large:[8,4]}[size];
      const options = cycles.map(weeks => plan(weeks,dose));
      // Preserve the reference storefront: value card first and selected.
      return {mode:'single', options, recommended:options[0], alternative:options[1], dose, dogs};
    }
    const eligible = WEEKS.filter(weeks => weeks <= 12 && Number.isSafeInteger(dose * weeks / 4)).map(weeks => plan(weeks,dose));
    // A merchandising guide, not a hard purchase ceiling: keep at least the
    // smallest exact supply even when the household needs more than 12 boxes.
    const displayedBoxLimit = Math.max(NORMAL_BOX_LIMIT, eligible[0].boxes);
    const options = eligible.filter(p => p.boxes <= displayedBoxLimit).slice(0, MAX_OPTIONS);
    const recommended = options.find(p => p.boxes >= 3 && p.boxes <= 4) || options[0];
    return {mode:'household', options, recommended, alternative:options.find(p => p !== recommended), dose, dogs};
  }
  function oneTime(quantity = 1) {
    const subtotal = quantity * PRICE.once;
    if (!Number.isSafeInteger(quantity) || quantity < 1 || !Number.isSafeInteger(subtotal)) throw new Error('Enter a whole number of boxes, starting from 1.');
    const shipping = PRICE.shipping;
    return { quantity, unit: PRICE.once, subtotal, shipping, total: subtotal + shipping, recurring: false };
  }
  function interval(days) {
    if (!Number.isInteger(days) || !WEEKS.includes(days / 7)) throw new Error('Choose a 4-, 8-, 12- or 24-week refill cycle.');
    return `${days / 7} weeks`;
  }
  function money(cents) { return '$' + (cents / 100).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  function shortNumber(value) { return Number(value.toFixed(2)).toLocaleString('en-SG'); }
  function dateAfter(days, base = new Date()) {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Singapore', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(base);
    const part = type => Number(parts.find(p => p.type === type).value);
    const date = new Date(Date.UTC(part('year'), part('month') - 1, part('day') + days));
    return new Intl.DateTimeFormat('en-SG', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date);
  }
  const api = { PRICE, configurePrice, DISCOUNTS, subscriptionPrice, subscriptionBoxes, cartShipping, cartDiscount, discountCodes, selectedCodes, validAllocation, validBase, validDiscount, DOSES, WEEKS, MAX_OPTIONS, MAX_DOGS, NORMAL_BOX_LIMIT, validate, daily, plan, recommend, oneTime, interval, money, shortNumber, dateAfter };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.HouseholdPlanner = api;
})(typeof globalThis !== 'undefined' ? globalThis : window);
(function (root) {
  'use strict';
  const P = root.HouseholdPlanner;
  const PLAN_IDS = Object.freeze({
    basic: Object.freeze({4:3092086967, 8:3092119735, 12:3092152503, 24:3092185271}),
    volume: Object.freeze({4:3092250807, 8:3092283575, 12:3092316343, 24:3092349111})
  });
  const LEGACY_PLAN_IDS = Object.freeze([3018883255,3018916023,3049619639,3049652407]);
  function editablePlan(id) { return Boolean(planWeeks(id)) || LEGACY_PLAN_IDS.includes(Number(id)); }
  function countsFromSizes(sizes) {
    if (!Array.isArray(sizes) || sizes.length < 1 || sizes.length > P.MAX_DOGS) throw new Error('Invalid household');
    const counts = {small: 0, medium: 0, large: 0};
    for (const size of sizes) {
      if (!Object.hasOwn(counts, size)) throw new Error('Incomplete household');
      counts[size]++;
    }
    return counts;
  }
  function selectedOffer(sizes, weeks) {
    const recommendation = P.recommend(countsFromSizes(sizes));
    return weeks == null ? recommendation.recommended : recommendation.options.find(row => row.weeks === Number(weeks)) || null;
  }
  function planId(row) {
    if (!row || !Number.isSafeInteger(row.boxes) || row.boxes < 1) return null;
    return PLAN_IDS[row.boxes >= 3 ? 'volume' : 'basic'][row.weeks] || null;
  }
  function planWeeks(id) {
    for (const plans of Object.values(PLAN_IDS)) for (const [weeks, plan] of Object.entries(plans)) {
      if (plan === Number(id)) return Number(weeks);
    }
    return null;
  }
  function dogProperties(sizes) {
    const c = countsFromSizes(sizes);
    return {
      _garrnish_sizes: sizes.join(','),
      _garrnish_small_dogs: String(c.small), _garrnish_medium_dogs: String(c.medium), _garrnish_large_dogs: String(c.large),
      _garrnish_total_dogs: String(sizes.length),
      _garrnish_segment: sizes.length === 1 ? 'single_' + sizes[0] : Object.values(c).filter(Boolean).length > 1 ? 'mixed_household' : 'multi_' + sizes[0]
    };
  }
  function properties(sizes, row) {
    if (!selectedOffer(sizes, row.weeks) || selectedOffer(sizes, row.weeks).boxes !== row.boxes) throw new Error('Invalid supply');
    return {
      ...dogProperties(sizes), _garrnish_household: '1',
      _garrnish_weeks: String(row.weeks), _garrnish_boxes: String(row.boxes)
    };
  }
  function managed(item) {
    return Number(item.product_id) === 8639414862007 && Boolean(item.selling_plan_allocation?.selling_plan?.id);
  }
  function restore(item, useAvailableSupply = false) {
    const sizes = String(item.properties?._garrnish_sizes || '').split(',');
    const row = selectedOffer(sizes, item.properties?._garrnish_weeks) || (useAvailableSupply ? selectedOffer(sizes) : null);
    if (!row) throw new Error('Supply no longer available');
    return {sizes, row};
  }
  function validLine(item) {
    try {
      const {sizes, row} = restore(item);
      const expected = properties(sizes, row);
      const a = item.selling_plan_allocation;
      return managed(item) && item.quantity === row.boxes && planId(row) === Number(a.selling_plan.id) &&
        P.validBase(item) &&
        Object.entries(expected).every(([k,v]) => String(item.properties[k]) === v);
    } catch (_) { return false; }
  }
  function matchesSelection(item, sizes, row, variantId) {
    if (!item || !row || Number(item.variant_id) !== Number(variantId) || !validLine(item)) return false;
    try {
      return item.quantity === row.boxes && Number(item.selling_plan_allocation.selling_plan.id) === planId(row) &&
        Object.entries(properties(sizes, row)).every(([key, value]) => String(item.properties[key]) === value);
    } catch (_) { return false; }
  }
  Object.assign(P, {PLAN_IDS, LEGACY_PLAN_IDS, editablePlan, countsFromSizes, selectedOffer, planId, planWeeks, dogProperties, properties, managed, restore, validLine, matchesSelection});
})(typeof globalThis !== 'undefined' ? globalThis : window);
